import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import isEqual from 'lodash/isEqual';

import { authSelectors } from '@ott/auth';
import { prepareRHFErrorsDeeply } from '@ott/first-error-shower';
import {
  actions as virtualCardActions,
  selectors as virtualCardSelectors,
} from '@ott/shared-virtual-card-logic';
import KM from '@ott/utility-km';

import { yupResolver } from '@hookform/resolvers/yup';
import { unwrapResult } from '@reduxjs/toolkit';

import { FORM_FIELDS } from '../helpers/constants';
import {
  formDataScheme,
  getOptionsFromPassangers,
  getPassengerInfo,
  isInnReady,
} from '../helpers/formHelpers';
import { FormData, InnRequest } from '../types';

import * as yup from 'yup';

type FormFields = typeof FORM_FIELDS[keyof typeof FORM_FIELDS];

type Props = {
  onSubmit: (data: FormData) => void;
  defaultValues: Partial<FormData>;
};

export const useUpgradeForm = ({ onSubmit, defaultValues }: Props) => {
  const [isINNVisible, setINNVisible] = useState(false);
  const [submitTime, setSubmitTime] = useState(Date.now());
  const innRef = useRef<InnRequest | null>(null);
  const cardUpgrade = useSelector(virtualCardSelectors.getCardUpgrade);
  const inn = useSelector(virtualCardSelectors.getInn);
  const passengers = useSelector(authSelectors.passengersSelector);

  const options = useMemo(
    () => (passengers ? getOptionsFromPassangers(passengers) : []),
    [passengers]
  );

  const isINNLoading = inn.loading;
  const isLoading = cardUpgrade.loading;

  const dispatch = useDispatch<any>();

  const {
    control,
    getValues,
    watch,
    formState: { errors, isValid, submitCount, isDirty, isSubmitted },
    setValue,
    handleSubmit,
    setFocus,
  } = useForm<FormData>({
    resolver: yupResolver<yup.AnyObjectSchema>(formDataScheme),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onDateChange = useCallback(
    (currentField: 'birthDate' | 'documentIssueDate') => {
      const date = getValues(currentField) ?? '';
      const nextField: FormFields =
        currentField === 'birthDate' ? FORM_FIELDS.DOCUMENT_NUMBER : FORM_FIELDS.DOCUMENT_UNIT_CODE;

      if (date.length === 8) {
        setFocus(nextField);
      }
    },
    [getValues, setFocus]
  );

  const handlePassengerSelect = useCallback(
    (valueId: string | null = '') => {
      const selectedPassenger = passengers?.find(
        ({ documentNumber }) => documentNumber === valueId
      );

      if (selectedPassenger) {
        const { documentNumber, firstName, birthDate, lastName, middleName } =
          getPassengerInfo(selectedPassenger);

        setFocus(FORM_FIELDS.DOCUMENT_ISSUE_DATE);
        setValue(FORM_FIELDS.FIRST_NAME, firstName);
        setValue(FORM_FIELDS.MIDDLE_NAME, middleName);
        setValue(FORM_FIELDS.DOCUMENT_NUMBER, documentNumber);
        setValue(FORM_FIELDS.BIRTH_DATE, birthDate);
        setValue(FORM_FIELDS.LAST_NAME, lastName);
      }
    },
    [passengers, setFocus, setValue]
  );

  const handleUpgradeCard = useCallback(
    (formData: FormData) => {
      KM('Virtual_Card_Uprid_Completed');

      if (isLoading || !isValid) {
        return false;
      }

      onSubmit(formData);

      return undefined;
    },
    [isLoading, isValid, onSubmit]
  );

  const preparedErrors = prepareRHFErrorsDeeply(errors);

  useEffect(() => {
    const subscribe = watch((data) => {
      const params: Required<InnRequest> = {
        birthDate: data.birthDate ?? '',
        documentNumber: data.documentNumber ?? '',
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        middleName: data.middleName ?? '',
      };

      const isUpdated = !isEqual(innRef.current, params);

      if (isUpdated && isInnReady(params)) {
        innRef.current = params;
        dispatch(virtualCardActions.getINN(params))
          .then(unwrapResult)
          .then(({ innData }: { innData: string }) => {
            if (innData) {
              const isINNUpdated = innData !== data[FORM_FIELDS.INN];
              if (isINNUpdated) {
                setValue(FORM_FIELDS.INN, innData);
              }
            } else {
              setINNVisible(true);
              setValue(FORM_FIELDS.INN, '');
            }
          })
          .catch(() => {
            setINNVisible(true);
            setValue(FORM_FIELDS.INN, '');
          });
      }
    });

    return () => subscribe.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (submitCount) {
      setSubmitTime(Date.now());
    }
  }, [submitCount]);

  return {
    isINNVisible,
    isINNLoading,
    isDirty,
    isLoading,
    isSubmitted,
    preparedErrors,
    options,
    submitTime,
    control,
    onDateChange,
    handleSubmit: handleSubmit(handleUpgradeCard),
    handlePassengerSelect,
    setValue,
  };
};
